import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  MapPin, 
  Eye, 
  Shield, 
  Key, 
  Lock, 
  MessageSquare, 
  Smartphone, 
  Globe,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Star,
  Crown,
  CheckCircle2,
  Info
} from 'lucide-react';

// Tool suggestions database with all details
const toolSuggestions = {
  has_location_services: {
    title: "Disable Location Services",
    description: "Prevent apps from tracking your physical location",
    tools: [
      {
        name: "iOS Settings",
        icon: "🍎",
        type: "built-in",
        rating: 5,
        description: "Settings → Privacy → Location Services",
        steps: ["Open Settings", "Tap Privacy & Security", "Tap Location Services", "Review each app", "Set to 'Never' or 'While Using'"],
        free: true
      },
      {
        name: "Android Settings",
        icon: "🤖",
        type: "built-in",
        rating: 5,
        description: "Settings → Location → App permissions",
        steps: ["Open Settings", "Tap Location", "Tap App location permissions", "Review each app", "Set to 'Don't allow' or 'Ask every time'"],
        free: true
      },
      {
        name: "Lockdown Mode (iOS)",
        icon: "🔒",
        type: "advanced",
        rating: 5,
        description: "Extreme protection for high-risk users",
        steps: ["Settings → Privacy & Security → Lockdown Mode", "Enable for maximum protection"],
        free: true,
        premium: true
      }
    ],
    quickTip: "Disable 'Precise Location' even for apps that need location - approximate location is usually sufficient."
  },
  has_ad_tracking: {
    title: "Disable Ad Tracking",
    description: "Stop advertisers from building profiles based on your activity",
    tools: [
      {
        name: "iOS App Tracking",
        icon: "🍎",
        type: "built-in",
        rating: 5,
        description: "Settings → Privacy → Tracking",
        steps: ["Open Settings", "Tap Privacy & Security", "Tap Tracking", "Disable 'Allow Apps to Request to Track'"],
        free: true
      },
      {
        name: "Android Ad ID",
        icon: "🤖",
        type: "built-in",
        rating: 5,
        description: "Settings → Privacy → Ads",
        steps: ["Open Settings", "Tap Privacy", "Tap Ads", "Tap 'Delete advertising ID'"],
        free: true
      },
      {
        name: "Blokada",
        icon: "🛡️",
        type: "app",
        rating: 4.5,
        description: "System-wide ad and tracker blocker",
        link: "https://blokada.org",
        steps: ["Download from blokada.org", "Enable VPN profile", "Block ads system-wide"],
        free: true
      },
      {
        name: "AdGuard",
        icon: "🟢",
        type: "app",
        rating: 4.5,
        description: "Premium ad blocking with custom filters",
        link: "https://adguard.com",
        steps: ["Download AdGuard", "Enable protection", "Configure custom filter lists"],
        free: false,
        price: "$2.49/mo"
      }
    ],
    quickTip: "Reset your advertising ID monthly to break tracking profiles."
  },
  uses_vpn: {
    title: "Use a VPN",
    description: "Encrypt your internet traffic and hide your IP address",
    tools: [
      {
        name: "Mullvad VPN",
        icon: "🟡",
        type: "app",
        rating: 5,
        description: "Privacy-focused, no-logs VPN. Anonymous accounts.",
        link: "https://mullvad.net",
        steps: ["Visit mullvad.net", "Generate account number (no email needed)", "Download app", "Enter account number", "Connect"],
        free: false,
        price: "€5/mo flat"
      },
      {
        name: "ProtonVPN",
        icon: "🟣",
        type: "app",
        rating: 4.5,
        description: "Swiss-based VPN with free tier available",
        link: "https://protonvpn.com",
        steps: ["Create Proton account", "Download ProtonVPN", "Login and connect", "Enable kill switch"],
        free: true,
        price: "Free tier / $4.99/mo"
      },
      {
        name: "NordVPN",
        icon: "🔵",
        type: "app",
        rating: 4.5,
        description: "Popular VPN with large server network",
        link: "https://nordvpn.com",
        steps: ["Create account at nordvpn.com", "Download app", "Login", "Connect to recommended server"],
        free: false,
        price: "$3.49/mo (2yr)"
      },
      {
        name: "Windscribe",
        icon: "🌀",
        type: "app",
        rating: 4,
        description: "Generous free tier with 10GB/month",
        link: "https://windscribe.com",
        steps: ["Sign up at windscribe.com", "Download app", "Login", "Connect"],
        free: true,
        price: "Free 10GB / $5.75/mo"
      }
    ],
    quickTip: "Always enable the kill switch feature to prevent data leaks if VPN disconnects."
  },
  has_strong_passwords: {
    title: "Use Strong Passwords",
    description: "Protect your accounts with unique, complex passwords",
    tools: [
      {
        name: "Bitwarden",
        icon: "🔐",
        type: "app",
        rating: 5,
        description: "Open-source password manager. Free tier excellent.",
        link: "https://bitwarden.com",
        steps: ["Create account at bitwarden.com", "Download browser extension", "Import existing passwords", "Generate new strong passwords", "Enable 2FA for vault"],
        free: true,
        price: "Free / $10/yr premium"
      },
      {
        name: "1Password",
        icon: "1️⃣",
        type: "app",
        rating: 5,
        description: "Premium password manager with family sharing",
        link: "https://1password.com",
        steps: ["Sign up at 1password.com", "Download apps", "Create vault", "Import passwords", "Use Watchtower for security"],
        free: false,
        price: "$2.99/mo"
      },
      {
        name: "Dashlane",
        icon: "🔷",
        type: "app",
        rating: 4.5,
        description: "Password manager with VPN included",
        link: "https://dashlane.com",
        steps: ["Create Dashlane account", "Install extension", "Import passwords", "Use Password Health feature"],
        free: true,
        price: "Free limited / $4.99/mo"
      },
      {
        name: "Avast Passwords",
        icon: "🟠",
        type: "app",
        rating: 4,
        description: "Free password manager from Avast",
        link: "https://avast.com/passwords",
        steps: ["Download Avast Passwords", "Create master password", "Import or add passwords", "Sync across devices"],
        free: true,
        price: "Free"
      },
      {
        name: "Apple Keychain",
        icon: "🍎",
        type: "built-in",
        rating: 4.5,
        description: "Built into Apple devices, syncs via iCloud",
        steps: ["Settings → Passwords", "Enable AutoFill Passwords", "Use suggested strong passwords", "Review Security Recommendations"],
        free: true
      }
    ],
    quickTip: "Use a unique password for every account. Password managers make this easy by generating and remembering them for you."
  },
  two_factor_enabled: {
    title: "Enable Two-Factor Authentication",
    description: "Add an extra layer of security beyond passwords",
    tools: [
      {
        name: "Microsoft Authenticator",
        icon: "Ⓜ️",
        type: "app",
        rating: 5,
        description: "Free 2FA app with cloud backup",
        link: "https://www.microsoft.com/authenticator",
        steps: ["Download Microsoft Authenticator", "Add account", "Scan QR code from service", "Enable cloud backup"],
        free: true
      },
      {
        name: "Google Authenticator",
        icon: "🔴",
        type: "app",
        rating: 4,
        description: "Simple, reliable 2FA from Google",
        link: "https://googleauthenticator.net",
        steps: ["Download Google Authenticator", "Tap + to add account", "Scan QR code", "Enter 6-digit code to verify"],
        free: true
      },
      {
        name: "Authy",
        icon: "🔴",
        type: "app",
        rating: 4.5,
        description: "2FA with encrypted cloud backups and multi-device sync",
        link: "https://authy.com",
        steps: ["Download Authy", "Register phone number", "Add 2FA accounts", "Enable backups", "Set up on multiple devices"],
        free: true
      },
      {
        name: "YubiKey",
        icon: "🔑",
        type: "hardware",
        rating: 5,
        description: "Physical security key - most secure option",
        link: "https://yubico.com",
        steps: ["Purchase YubiKey", "Register with supported services", "Tap key when prompted", "Keep backup key safe"],
        free: false,
        price: "$25-70"
      },
      {
        name: "Duo Mobile",
        icon: "🟢",
        type: "app",
        rating: 4.5,
        description: "Enterprise-grade 2FA with push notifications",
        link: "https://duo.com",
        steps: ["Download Duo Mobile", "Add accounts via QR code", "Approve logins via push notification"],
        free: true
      }
    ],
    quickTip: "Hardware keys like YubiKey are phishing-resistant. For critical accounts (email, banking), consider using one."
  },
  uses_encrypted_messaging: {
    title: "Use Encrypted Messaging",
    description: "Keep your conversations private with end-to-end encryption",
    tools: [
      {
        name: "Signal",
        icon: "💬",
        type: "app",
        rating: 5,
        description: "Gold standard for encrypted messaging. Open source.",
        link: "https://signal.org",
        steps: ["Download Signal", "Register with phone number", "Verify contacts' safety numbers", "Enable disappearing messages", "Set screen lock"],
        free: true
      },
      {
        name: "WhatsApp",
        icon: "🟢",
        type: "app",
        rating: 3.5,
        description: "E2E encrypted, but owned by Meta (collects metadata)",
        link: "https://whatsapp.com",
        steps: ["Download WhatsApp", "Verify encryption in chat info", "Enable disappearing messages", "Review privacy settings"],
        free: true
      },
      {
        name: "Telegram (Secret Chats)",
        icon: "✈️",
        type: "app",
        rating: 3,
        description: "Secret Chats are E2E encrypted (regular chats are not!)",
        link: "https://telegram.org",
        steps: ["Download Telegram", "Start New Secret Chat (NOT regular chat)", "Enable self-destruct timer", "Verify encryption keys"],
        free: true
      },
      {
        name: "Session",
        icon: "🟣",
        type: "app",
        rating: 4.5,
        description: "No phone number required. Decentralized.",
        link: "https://getsession.org",
        steps: ["Download Session", "Create Session ID (no phone needed)", "Share ID with contacts", "Enable disappearing messages"],
        free: true
      },
      {
        name: "ProtonMail",
        icon: "📧",
        type: "app",
        rating: 5,
        description: "Encrypted email service based in Switzerland",
        link: "https://proton.me",
        steps: ["Create account at proton.me", "Download apps", "Use for sensitive emails", "Enable 2FA"],
        free: true,
        price: "Free / $3.99/mo"
      }
    ],
    quickTip: "Signal is recommended by privacy experts including Edward Snowden. Regular SMS is never encrypted."
  },
  reviews_app_permissions: {
    title: "Review App Permissions",
    description: "Regularly audit what access your apps have",
    tools: [
      {
        name: "iOS Privacy Report",
        icon: "🍎",
        type: "built-in",
        rating: 5,
        description: "See which apps access your data and how often",
        steps: ["Settings → Privacy & Security → App Privacy Report", "Enable if not already", "Review which apps access what", "Revoke unnecessary permissions"],
        free: true
      },
      {
        name: "Android Permission Manager",
        icon: "🤖",
        type: "built-in",
        rating: 4.5,
        description: "Control app permissions from one place",
        steps: ["Settings → Privacy → Permission Manager", "Review each permission category", "Revoke unnecessary access", "Check 'Unused apps' section"],
        free: true
      },
      {
        name: "Exodus Privacy",
        icon: "🔍",
        type: "web",
        rating: 4.5,
        description: "Audit Android apps for trackers and permissions",
        link: "https://exodus-privacy.eu.org",
        steps: ["Visit exodus-privacy.eu.org", "Search for your apps", "Review tracker count", "Uninstall high-tracker apps"],
        free: true
      },
      {
        name: "AppCensus",
        icon: "📊",
        type: "web",
        rating: 4,
        description: "Research app privacy behaviors",
        link: "https://appcensus.io",
        steps: ["Visit appcensus.io", "Search for apps you use", "Review data collection practices"],
        free: true
      }
    ],
    quickTip: "Review permissions monthly. Many apps request more access than they need - deny if not essential."
  },
  uses_privacy_browser: {
    title: "Use a Privacy Browser",
    description: "Browse the web without being tracked",
    tools: [
      {
        name: "Brave Browser",
        icon: "🦁",
        type: "app",
        rating: 5,
        description: "Chromium-based with built-in ad/tracker blocking",
        link: "https://brave.com",
        steps: ["Download from brave.com", "Import bookmarks", "Enable Shields (on by default)", "Set as default browser"],
        free: true
      },
      {
        name: "Firefox",
        icon: "🦊",
        type: "app",
        rating: 4.5,
        description: "Open-source browser with Enhanced Tracking Protection",
        link: "https://firefox.com",
        steps: ["Download Firefox", "Enable Strict tracking protection", "Install uBlock Origin", "Enable DNS over HTTPS"],
        free: true
      },
      {
        name: "DuckDuckGo Browser",
        icon: "🦆",
        type: "app",
        rating: 4.5,
        description: "Mobile browser with Fire Button to clear all data",
        link: "https://duckduckgo.com/app",
        steps: ["Download DuckDuckGo app", "Set as default browser", "Use Fire Button after sessions", "Enable Email Protection"],
        free: true
      },
      {
        name: "Tor Browser",
        icon: "🧅",
        type: "app",
        rating: 5,
        description: "Maximum anonymity through onion routing",
        link: "https://torproject.org",
        steps: ["Download from torproject.org only", "Connect to Tor network", "Don't login to personal accounts", "Don't maximize window"],
        free: true
      },
      {
        name: "LibreWolf",
        icon: "🐺",
        type: "app",
        rating: 4.5,
        description: "Privacy-hardened Firefox fork",
        link: "https://librewolf.net",
        steps: ["Download from librewolf.net", "Import Firefox profile", "Review preset privacy settings"],
        free: true
      }
    ],
    quickTip: "Use Brave or Firefox for daily browsing. Use Tor for sensitive research. Never use Tor with personal accounts."
  }
};

export default function PrivacyChecklistEnhanced({ checks, onToggle, onUpdateScore, loading }) {
  const { user } = useAuth();
  const [expandedItem, setExpandedItem] = useState(null);

  const checkItems = [
    { key: 'has_location_services', label: 'Location Services Disabled', icon: MapPin, inverted: true },
    { key: 'has_ad_tracking', label: 'Ad Tracking Disabled', icon: Eye, inverted: true },
    { key: 'uses_vpn', label: 'Using VPN', icon: Shield, inverted: false },
    { key: 'has_strong_passwords', label: 'Strong Passwords', icon: Key, inverted: false },
    { key: 'two_factor_enabled', label: 'Two-Factor Auth', icon: Lock, inverted: false },
    { key: 'uses_encrypted_messaging', label: 'Encrypted Messaging', icon: MessageSquare, inverted: false },
    { key: 'reviews_app_permissions', label: 'Review App Permissions', icon: Smartphone, inverted: false },
    { key: 'uses_privacy_browser', label: 'Privacy Browser', icon: Globe, inverted: false },
  ];

  const toggleExpand = (key) => {
    setExpandedItem(expandedItem === key ? null : key);
  };

  return (
    <div className="space-y-3">
      {checkItems.map((item) => {
        const isEnabled = item.inverted ? !checks[item.key] : checks[item.key];
        const isExpanded = expandedItem === item.key;
        const suggestions = toolSuggestions[item.key];
        
        return (
          <div 
            key={item.key}
            className={`rounded-xl border transition-all duration-300 ${
              isEnabled 
                ? 'bg-[#00FF94]/5 border-[#00FF94]/20' 
                : 'bg-[#0a0a0a] border-[#262626]'
            }`}
          >
            {/* Main Row */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer"
              onClick={() => toggleExpand(item.key)}
            >
              <div className="flex items-center gap-3 flex-1">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isEnabled ? 'bg-[#00FF94]/10' : 'bg-[#171717]'
                }`}>
                  <item.icon className={`w-5 h-5 ${isEnabled ? 'text-[#00FF94]' : 'text-gray-500'}`} />
                </div>
                <div className="flex-1">
                  <span className={`font-medium ${isEnabled ? 'text-white' : 'text-gray-400'}`}>
                    {item.label}
                  </span>
                  {!isEnabled && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      Click to see recommended tools
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-[#00FF94] p-1 h-auto"
                  onClick={(e) => { e.stopPropagation(); toggleExpand(item.key); }}
                >
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </Button>
                <Switch 
                  checked={isEnabled}
                  onCheckedChange={() => onToggle(item.key)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && suggestions && (
              <div className="px-4 pb-4 border-t border-[#262626] pt-4">
                <div className="mb-4">
                  <h4 className="text-white font-medium mb-1">{suggestions.title}</h4>
                  <p className="text-sm text-gray-500">{suggestions.description}</p>
                </div>

                {/* Quick Tip */}
                <div className="bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-[#00FF94] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-400">
                      <span className="text-[#00FF94] font-medium">Quick Tip:</span> {suggestions.quickTip}
                    </p>
                  </div>
                </div>

                {/* Tool Suggestions */}
                <div className="space-y-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Recommended Tools</p>
                  
                  {suggestions.tools.slice(0, user?.is_premium ? suggestions.tools.length : 2).map((tool, index) => (
                    <div 
                      key={index}
                      className="bg-[#171717] rounded-lg p-4 hover:bg-[#1a1a1a] transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{tool.icon}</span>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{tool.name}</span>
                              <Badge className={`text-xs ${
                                tool.type === 'built-in' ? 'bg-blue-500/20 text-blue-400' :
                                tool.type === 'hardware' ? 'bg-purple-500/20 text-purple-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {tool.type}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1 mt-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`w-3 h-3 ${i < Math.floor(tool.rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} 
                                />
                              ))}
                              <span className="text-xs text-gray-500 ml-1">{tool.rating}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {tool.free ? (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Free</Badge>
                          ) : (
                            <span className="text-xs text-gray-400">{tool.price}</span>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-400 mb-3">{tool.description}</p>
                      
                      {/* Steps - Show for premium or first tool */}
                      {(user?.is_premium || index === 0) && tool.steps && (
                        <div className="space-y-1 mb-3">
                          {tool.steps.map((step, stepIndex) => (
                            <div key={stepIndex} className="flex items-center gap-2 text-xs">
                              <div className="w-4 h-4 rounded-full bg-[#262626] flex items-center justify-center text-gray-500 flex-shrink-0">
                                {stepIndex + 1}
                              </div>
                              <span className="text-gray-500">{step}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {tool.link && (
                        <a 
                          href={tool.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#00FF94] hover:underline"
                        >
                          Visit website <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  ))}

                  {/* Premium Upsell */}
                  {!user?.is_premium && suggestions.tools.length > 2 && (
                    <div className="bg-gradient-to-r from-[#00FF94]/5 to-transparent border border-[#00FF94]/20 rounded-lg p-4 text-center">
                      <Crown className="w-6 h-6 text-[#00FF94] mx-auto mb-2" />
                      <p className="text-sm text-gray-400 mb-2">
                        {suggestions.tools.length - 2} more tools available for Premium
                      </p>
                      <Button 
                        size="sm" 
                        className="btn-primary"
                        onClick={() => window.location.href = '/pricing'}
                      >
                        Unlock All Tools
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
